import * as React from 'react';
import { scrollToAnchor } from '@cian/newbuilding-utils';
import { useSelector, useDispatch } from 'react-redux';

import { Genplan } from '../../components/Genplan';
import { House } from '../../components/Genplan/components/House';
import { HouseTooltip } from '../../components/Genplan/components/HouseTooltip';
import { getGenplanSelector, getIsFloorPlanAvailable, getNewbuildingId } from '../../selectors/newbuilding';
import { setFloorPlanHouseId, setOpenFloorPlan } from '../../actions/floorPlan';
import { GenplanControls } from '../../components/GenplanControls';
import { isFloorPlanEnabled } from '../../selectors/floorPlan';
import {
  trackShowGenplan,
  trackHouseHover,
  trackHouseLinkClick,
  trackGenplanReliableLabelClick,
  trackGenplanEscrowLabelClick,
} from './tracking';
import { LayoutBlock } from '../../components/LayoutBlock';

export function GenplanContainer() {
  const dispatch = useDispatch();

  const genplan = useSelector(getGenplanSelector);
  const newbuildingId = useSelector(getNewbuildingId);
  const isPlanEnabled = useSelector(isFloorPlanEnabled);
  const isFloorPlanAvailable = useSelector(getIsFloorPlanAvailable);
  const [viewedHouses, setViewedHouses] = React.useState<number[]>([]);

  const onHover = React.useCallback(
    (houseId: number) => {
      /* istanbul ignore else */
      if (!viewedHouses.includes(houseId)) {
        trackHouseHover(newbuildingId, houseId);
        setViewedHouses([...viewedHouses, houseId]);
      }
    },
    [newbuildingId, viewedHouses],
  );

  const onHouseLinkClick = React.useCallback(
    (houseId: number, roomType: string) => {
      trackHouseLinkClick(newbuildingId, houseId, roomType);
    },
    [newbuildingId],
  );
  /* istanbul ignore next */
  const onReliableLabelClick = (houseId: number) => (event: React.MouseEvent<HTMLElement>) => {
    scrollToAnchor({
      anchor: '#reliable',
      offset: 0,
      hasRelocation: false,
      hasAnimation: true,
    })(event);
    trackGenplanReliableLabelClick(newbuildingId, houseId);
  };

  /* istanbul ignore next */
  const onEscrowLabelClick = (houseId: number) => (event: React.MouseEvent<HTMLElement>) => {
    scrollToAnchor({
      anchor: '#escrow',
      offset: 100,
      hasRelocation: false,
      hasAnimation: true,
    })(event);
    trackGenplanEscrowLabelClick(newbuildingId, houseId);
  };

  if (!genplan) {
    return null;
  }

  const onFloorPlanClick = (houseId?: number) => () => {
    dispatch(setFloorPlanHouseId(houseId || null));
    dispatch(setOpenFloorPlan(true));
  };

  const { image, isHousesNamesEnabled, houses } = genplan;

  return (
    <LayoutBlock>
      <Genplan
        image={image}
        onShow={trackShowGenplan}
        renderHouses={(containerWidth, containerHeight) =>
          houses.map(({ name, houseInfo, stats, isReliable, isEscrow, isSalesStart, ...house }) => (
            <House
              key={house.id}
              {...house}
              name={name}
              isReliable={isReliable}
              isEscrow={Boolean(isEscrow)}
              isSalesStart={Boolean(isSalesStart)}
              emptyStats={!stats}
              showTitle={isHousesNamesEnabled}
              containerWidth={containerWidth}
              containerHeight={containerHeight}
              tooltip={
                <HouseTooltip
                  name={name}
                  houseId={house.id}
                  houseInfo={houseInfo}
                  stats={stats}
                  isReliable={isReliable}
                  isEscrow={Boolean(isEscrow)}
                  isSalesStart={Boolean(isSalesStart)}
                  onLinkClick={onHouseLinkClick}
                  onReliableLabelClick={onReliableLabelClick(house.id)}
                  onEscrowLabelClick={onEscrowLabelClick(house.id)}
                  isFloorPlanEnabled={isPlanEnabled && isFloorPlanAvailable}
                  onFloorPlanClick={onFloorPlanClick(house.id)}
                />
              }
              onHover={() => onHover(house.id)}
            />
          ))
        }
      >
        <GenplanControls
          isFloorPlanEnabled={isPlanEnabled}
          isFloorPlanAvailable={isFloorPlanAvailable}
          onFloorPlanClick={onFloorPlanClick()}
        />
      </Genplan>
    </LayoutBlock>
  );
}
